import { cn } from "@heroui/react";
import * as BootstrapIcons from "react-bootstrap-icons";
import Image from "next/image";

import { Option } from "@/types/dataSchema";

import { getIconComponent, Sizes } from "../utils/optionUtils";

interface OptionButtonProps {
  option: Option;
  index: number;
  isSelected: boolean;
  isRevealed: boolean;
  isMultiSelect: boolean;
  editValues: { [key: string]: string };
  buttonSize: string;
  size: Sizes;
  onSelectOption: (optionValue: string, editable?: boolean) => void;
}

export const OptionButton: React.FC<OptionButtonProps> = ({
  option,
  index,
  isSelected,
  isRevealed,
  isMultiSelect,
  editValues,
  buttonSize,
  size,
  onSelectOption,
}) => {
  const IconComponent = getIconComponent(option);
  const isEditable = !!option.editable;

  // Show custom text if available, otherwise show the default label
  const displayLabel = editValues[option.value]
    ? `${option.label}: ${editValues[option.value]}`
    : option.label;

  return (
    <button
      key={option.value}
      className={cn(
        "e2e-option-button", // Class for E2E testing
        "group flex flex-col items-start justify-center rounded-xl border-2 transition-all duration-300 ease-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 hover:shadow-[0_8px_25px_rgba(59,130,246,0.4)] relative min-w-40 overflow-hidden backdrop-blur-sm text-left",
        // Reveal animation classes
        isRevealed
          ? "opacity-100 translate-y-0 blur-0 scale-100 animate-reveal"
          : "opacity-0 translate-y-4 blur-sm scale-95",
        isSelected
          ? "border-blue-400 bg-gradient-to-br from-blue-500/30 to-purple-500/20 text-white shadow-xl scale-105 animate-soft-glow"
          : "border-gray-600/50 bg-gradient-to-br from-slate-800/60 to-slate-700/40 hover:bg-gradient-to-br hover:from-slate-700/70 hover:to-slate-600/50 text-gray-200 hover:text-white hover:border-gray-500",
        buttonSize,
      )}
      style={{
        transitionDelay: isRevealed ? `${index * 100}ms` : "0ms",
        animationDelay: isRevealed ? `${index * 100}ms` : "0ms",
      }}
      type="button"
      onClick={() => onSelectOption(option.value, isEditable)}
    >
      {/* Enhanced shine effect overlay */}
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-r from-transparent via-blue-300/20 to-transparent pointer-events-none",
          "transform -translate-x-full", // Start completely off-screen
          isRevealed && "animate-shine",
        )}
        style={{
          animationDelay: `${index * 50 + 100}ms`,
          animationFillMode: "forwards", // Stay at final position
        }}
      />

      {/* Multi-select checkbox */}
      {isMultiSelect && (
        <div
          className={cn(
            "absolute top-3 right-3 w-5 h-5 border-2 rounded-md flex items-center justify-center transition-all duration-200 shadow-lg",
            isSelected
              ? "bg-blue-500 border-blue-400 shadow-blue-500/50"
              : "border-gray-400/50 group-hover:border-gray-300 backdrop-blur-sm",
          )}
        >
          {isSelected && (
            <BootstrapIcons.CheckLg className="w-3 h-3 text-white drop-shadow-sm" />
          )}
        </div>
      )}

      {/* Content */}
      <div
        className={cn(
          "flex items-center gap-3 justify-star w-full",
          size === Sizes.LARGE
            ? "flex-col min-h-32 justify-center"
            : "flex-row",
          option.customImage ? "items-center flex-col-reverse" : "",
        )}
      >
        {/* Icon or Custom Image */}
        {option.customImage ? (
          <div
            className={cn(
              "mb-2",
              "h-10",
              "w-full",
              "flex",
              "items-center",
              "justify-center",
              "relative",
            )}
          >
            <Image
              fill
              alt={option.label}
              className="object-contain"
              src={option.customImage}
            />
          </div>
        ) : IconComponent ? (
          <div
            className={cn(
              "mb-2",
              "h-11",
              "w-11",
              "flex",
              "items-center",
              "justify-center",
            )}
          >
            <IconComponent
              className="h-11 w-11"
              style={{
                color: option.color || "currentColor",
              }}
            />
          </div>
        ) : null}

        {/* Label */}
        <div
          className={cn(
            "text-md text-center font-medium",
            isSelected
              ? "text-white drop-shadow-sm"
              : "text-gray-200 group-hover:text-white",
            size === Sizes.LARGE ? "text-lg 3xl:text-xl" : "",
            option.customImage ? "text-center" : "text-left",
          )}
        >
          {displayLabel}
        </div>
      </div>

      {/* Tooltip */}
      <span
        className={cn(
          "text-xs mt-2",
          isSelected
            ? "text-blue-100 drop-shadow-sm"
            : "text-gray-400 group-hover:text-gray-300",
          size === Sizes.LARGE ? "text-medium text-center w-full" : "",
        )}
      >
        {option.tooltip}
      </span>
    </button>
  );
};
